import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ActionStatus, ClientAction, Opportunity } from '../client-actions.model';

const SOURCE_TYPE_OPTIONS = [
  { label: 'AI Agent', value: 'aiAgent' },
  { label: 'Human', value: 'human' },
];

const STATUS_OPTIONS: { label: string; value: ActionStatus }[] = [
  { label: 'To Do', value: 'To Do' },
  { label: 'In Progress', value: 'In Progress' },
  { label: 'Done', value: 'Done' },
  { label: 'Blocked', value: 'Blocked' },
  { label: 'Withdrawn', value: 'Withdrawn' },
];

@Component({
  selector: 'bai-client-action-add-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, Button, InputTextModule, SelectModule],
  template: `
    <div class="flex flex-col gap-4 p-2">
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="ca-action">
          Action <span class="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id="ca-action"
          pInputText
          [(ngModel)]="form().action"
          (ngModelChange)="patchForm('action', $event)"
          placeholder="Describe the action"
          class="w-full"
          aria-required="true"
        />
        @if (submitted() && !form().action) {
          <span class="text-xs text-red-500" role="alert">Required</span>
        }
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="ca-fa-name">
          Financial Assistant Name <span class="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id="ca-fa-name"
          pInputText
          [(ngModel)]="form().financialAssistantName"
          (ngModelChange)="patchForm('financialAssistantName', $event)"
          placeholder="e.g. Sarah Chen"
          class="w-full"
          aria-required="true"
        />
        @if (submitted() && !form().financialAssistantName) {
          <span class="text-xs text-red-500" role="alert">Required</span>
        }
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="ca-source">
          Source <span class="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id="ca-source"
          pInputText
          [(ngModel)]="form().source"
          (ngModelChange)="patchForm('source', $event)"
          placeholder="e.g. BrightAI Analysis, Manual"
          class="w-full"
          aria-required="true"
        />
        @if (submitted() && !form().source) {
          <span class="text-xs text-red-500" role="alert">Required</span>
        }
      </div>

      @if (opportunityOptions.length > 0) {
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700" for="ca-opportunity">
            Linked Opportunity
          </label>
          <p-select
            inputId="ca-opportunity"
            [options]="opportunityOptions"
            [(ngModel)]="form().opportunityId"
            (ngModelChange)="patchForm('opportunityId', $event)"
            optionLabel="label"
            optionValue="value"
            placeholder="Select opportunity (optional)"
            styleClass="w-full"
            [showClear]="true"
          />
        </div>
      }

      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700" for="ca-planned">
            Planned Date <span class="text-red-500" aria-hidden="true">*</span>
          </label>
          <input
            id="ca-planned"
            pInputText
            type="date"
            [(ngModel)]="form().plannedDate"
            (ngModelChange)="patchForm('plannedDate', $event)"
            class="w-full"
            aria-required="true"
          />
          @if (submitted() && !form().plannedDate) {
            <span class="text-xs text-red-500" role="alert">Required</span>
          }
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700" for="ca-deadline">
            Deadline <span class="text-red-500" aria-hidden="true">*</span>
          </label>
          <input
            id="ca-deadline"
            pInputText
            type="date"
            [(ngModel)]="form().deadline"
            (ngModelChange)="patchForm('deadline', $event)"
            class="w-full"
            aria-required="true"
          />
          @if (submitted() && !form().deadline) {
            <span class="text-xs text-red-500" role="alert">Required</span>
          }
        </div>
      </div>

      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700" for="ca-source-type">
            Source Type <span class="text-red-500" aria-hidden="true">*</span>
          </label>
          <p-select
            inputId="ca-source-type"
            [options]="sourceTypeOptions"
            [(ngModel)]="form().sourceType"
            (ngModelChange)="patchForm('sourceType', $event)"
            optionLabel="label"
            optionValue="value"
            placeholder="Select type"
            styleClass="w-full"
            aria-required="true"
          />
          @if (submitted() && !form().sourceType) {
            <span class="text-xs text-red-500" role="alert">Required</span>
          }
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700" for="ca-status">
            Status <span class="text-red-500" aria-hidden="true">*</span>
          </label>
          <p-select
            inputId="ca-status"
            [options]="statusOptions"
            [(ngModel)]="form().status"
            (ngModelChange)="patchForm('status', $event)"
            optionLabel="label"
            optionValue="value"
            placeholder="Select status"
            styleClass="w-full"
            aria-required="true"
          />
          @if (submitted() && !form().status) {
            <span class="text-xs text-red-500" role="alert">Required</span>
          }
        </div>
      </div>

      <div class="flex justify-end gap-2 pt-2">
        <p-button label="Cancel" severity="secondary" (onClick)="cancel()" />
        <p-button label="Add" (onClick)="submit()" />
      </div>
    </div>
  `,
})
export class ClientActionAddDialogComponent {
  private _ref = inject(DynamicDialogRef);
  private _config = inject(DynamicDialogConfig);

  readonly sourceTypeOptions = SOURCE_TYPE_OPTIONS;
  readonly statusOptions = STATUS_OPTIONS;
  readonly submitted = signal(false);
  readonly opportunityOptions: { label: string; value: string }[];

  readonly form = signal<Partial<Omit<ClientAction, 'id' | 'createdDate'>>>({
    action: '',
    financialAssistantName: '',
    source: '',
    opportunityId: null,
    plannedDate: '',
    deadline: '',
    sourceType: undefined,
    status: 'To Do',
  });

  constructor() {
    const opportunities: Opportunity[] = this._config.data?.opportunities ?? [];
    this.opportunityOptions = opportunities.map((o) => ({
      label: o.opportunityDescription,
      value: o.id,
    }));
  }

  patchForm(key: string, value: unknown): void {
    this.form.update((f) => ({ ...f, [key]: value }));
  }

  cancel(): void {
    this._ref.close(null);
  }

  submit(): void {
    this.submitted.set(true);
    const f = this.form();
    if (!f.action || !f.financialAssistantName || !f.source || !f.plannedDate || !f.deadline || !f.sourceType || !f.status) return;

    const result: Omit<ClientAction, 'id'> = {
      action: f.action,
      financialAssistantName: f.financialAssistantName,
      source: f.source,
      opportunityId: f.opportunityId ?? null,
      plannedDate: f.plannedDate,
      deadline: f.deadline,
      sourceType: f.sourceType,
      status: f.status,
      createdDate: new Date().toISOString().slice(0, 10),
    };
    this._ref.close(result);
  }
}
